/**
 * The responsibility area of ONE task row (RESP2f).
 *
 * ## Why a component and not two inline blocks
 *
 * The task page renders both layouts at once — a table above `md:` and
 * a card stack below — so every row's area would otherwise be formatted
 * twice, in two files' worth of JSX, with four states each. That is how
 * a retired area ends up marked in the table and unmarked on a phone.
 *
 * ## Why it holds no logic of its own
 *
 * Resolution lives in `useCategoryDisplay` (RESP2d), which four other
 * surfaces already share — the report tables, the report workspace, the
 * task detail card. This adds a fifth reader, not a fifth opinion. In
 * particular the four states and their wording are decided there:
 *
 * * **known** — icon, label, the organisation's own colour.
 * * **retired** — the same, plus the word "Inaktiv". The row is fine;
 *   the area simply is not offered for new work any more.
 * * **unassigned** — a neutral phrase. Never an empty cell, which reads
 *   as a rendering bug rather than as an answer.
 * * **unresolvable** — a neutral phrase, never the raw key. This should
 *   be unreachable (the composite foreign key guarantees the area
 *   exists), so reaching it means the catalog did not load — worth
 *   saying honestly rather than papering over with a slug.
 *
 * ## What is never rendered
 *
 * The technical key, the slug, the area id. The key travels in the URL
 * and in the API; it is not a name, and showing one invites somebody to
 * type it somewhere.
 */
import React from "react";

import { useCategoryDisplay } from "./ActivityCategoryChip";

export default function TaskActivityCategoryCell({ code, className = "" }) {
  const { label, icon: Icon, isInactive, loading, state } =
    useCategoryDisplay(code);

  // While the catalog is in flight the row would otherwise flash the
  // "not available" phrase, which is a data-integrity statement and not
  // something to say about a list that is simply still loading. A task
  // with NO area needs no catalog, so it renders immediately.
  if (loading && state !== "unassigned") {
    return (
      <span
        className={"inline-flex items-center gap-1.5 " + className}
        aria-hidden="true"
      >
        <span className="h-3.5 w-3.5 shrink-0 animate-pulse rounded bg-muted" />
        <span className="h-3 w-20 animate-pulse rounded bg-muted" />
      </span>
    );
  }

  return (
    <span
      // `max-w-full` matters more than it looks: this sits in a grid
      // cell on the phone card, and a grid item's default
      // `min-width: auto` lets its content push the track wider than
      // the screen. Measured in Chromium at 390px — a long area name
      // overflowed the viewport by 115px without it, and `truncate`
      // alone did nothing because the box it was truncating INTO had
      // already grown.
      className={
        "inline-flex min-w-0 max-w-full items-center gap-1.5 " + className
      }
      // The full text, for a label the cell had to truncate. Not the
      // only source of the information — the label itself is text in
      // the DOM — so this is an aid, never the answer.
      title={isInactive ? `${label} (inaktiv)` : label}
    >
      <Icon
        aria-hidden="true"
        className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
        strokeWidth={1.75}
      />
      <span className="min-w-0 truncate text-[12px] text-foreground dark:text-[#c5cbe0]">
        {label}
      </span>
      {isInactive ? (
        // A word, not a shade. Somebody who cannot see the colour still
        // has to learn that this area is retired, and it is part of the
        // row's text so a screen reader reaches it in order.
        <span className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
          Inaktiv
        </span>
      ) : null}
    </span>
  );
}
